import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData, updateUserProfileData, fetchSecurityQuestions, getGoogleMapsApiKey } from "../../services/api";
import Header from "../course/layout/Header";
import Loader from '../../images/loader.png';

const ProfileEdit = () => {
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    address: '',
    email: ''
  });

  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [questions, setQuestions] = useState([
    { question: "", answer: "" },
    { question: "", answer: "" },
    { question: "", answer: "" }
  ]);
  const [errors, setErrors] = useState({});
  const [questionErrors, setQuestionErrors] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [verificationAnswers, setVerificationAnswers] = useState(['', '', '']);
  const [verificationErrors, setVerificationErrors] = useState(['', '', '']);
  const [verifiedQuestions, setVerifiedQuestions] = useState([false, false, false]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [enableEditCheckboxes, setEnableEditCheckboxes] = useState([false, false, false]);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('token');
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      // Check if test is completed
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (user.test_status === '1') {
        setIsTestCompleted(true);
      }
      loadUserData();
      loadGoogleMapsApiKey();
    }
  }, [navigate]);

  useEffect(() => {
    if (googleMapsApiKey && !isGoogleMapsLoaded) {
      loadGoogleMapsScript();
    }
  }, [googleMapsApiKey, isGoogleMapsLoaded]);

  useEffect(() => {
    if (isGoogleMapsLoaded && addressInputRef.current && !autocompleteRef.current) {
      // Add a small delay to ensure the DOM is fully ready
      setTimeout(() => {
        initializeAutocomplete();
      }, 100);
    }
  }, [isGoogleMapsLoaded]);

  // Cleanup autocomplete on unmount
  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  const loadGoogleMapsApiKey = async () => {
    try {
      const response = await getGoogleMapsApiKey();
      
      if (response.data) {
        setGoogleMapsApiKey(response.data.data);
      } else {
        // Fallback to environment variable if backend doesn't have the key
        const fallbackKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (fallbackKey) {
          setGoogleMapsApiKey(fallbackKey);
        }
      }
    } catch (error) {
      // Fallback to environment variable if backend endpoint doesn't exist
      const fallbackKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (fallbackKey) {
        setGoogleMapsApiKey(fallbackKey);
      }
    }
  };

  const loadGoogleMapsScript = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    if (!googleMapsApiKey) {
      return;
    }

    // Remove any existing Google Maps script
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsGoogleMapsLoaded(true);
    };
    script.onerror = () => {
      // Silent error handling
    };
    document.head.appendChild(script);
  };

  const initializeAutocomplete = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    if (!addressInputRef.current) {
      return;
    }

    if (autocompleteRef.current) {
      return;
    }

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id']
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (place && place.address_components) {
          let streetNumber = '';
          let streetName = '';
          let city = '';
          let state = '';
          let zipcode = '';

          place.address_components.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            }
            if (types.includes('route')) {
              streetName = component.long_name;
            }
            if (types.includes('locality')) {
              city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            }
            if (types.includes('postal_code')) {
              zipcode = component.long_name;
            }
          });

          if (streetNumber && streetName && city && state && zipcode) {
            const formattedAddress = `${streetNumber} ${streetName}, ${city}, ${state}, ${zipcode}`;
            setFormData(prev => ({
              ...prev,
              address: formattedAddress
            }));
            if (errors.address) {
              setErrors(prev => ({
                ...prev,
                address: ''
              }));
            }
          } else {
            setFormData(prev => ({
              ...prev,
              address: ''
            }));
            setErrors(prev => ({
              ...prev,
              address: 'Provide Valid address'
            }));
          }
        }
      });
    } catch (error) {
      // Silent error handling
    }
  };

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      const token = sessionStorage.getItem("token");
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      console.log('Loading user data with token:', token);
      
      // Load user profile data
      const userResponse = await getUserData(token);
      console.log('User data response:', userResponse.data);
      
      if (!userResponse.data || userResponse.data.status !== 'success' || !userResponse.data.user) {
        throw new Error('Invalid user data response');
      }
      
      const userData = userResponse.data.user;
      console.log('User data being set:', userData);
      
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        address: userData.address || '',
        email: userData.email || ''
      });
      
      // Load security questions
      console.log('Loading security questions...');
      const questionsResponse = await fetchSecurityQuestions(token);
      console.log('Security questions response:', questionsResponse.data);
      
      if (questionsResponse.data && questionsResponse.data.questions && Array.isArray(questionsResponse.data.questions)) {
        setSecurityQuestions(questionsResponse.data.questions);
        console.log('Security questions loaded:', questionsResponse.data.questions);
      } else {
        console.warn('Invalid security questions response:', questionsResponse.data);
        setSecurityQuestions([]);
      }
      
      // Set user's security questions
      if (userData.security_questions && userData.security_questions.length > 0) {
        const userQuestions = userData.security_questions.map(sq => ({
          question: sq.question,
          answer: sq.answer,
          id: sq.id // Store the ID for updates
        }));
        setQuestions(userQuestions);
        setOriginalQuestions(userQuestions);
        console.log('User security questions set:', userQuestions);
      } else {
        console.log('No security questions found for user');
        // Initialize with empty questions if user doesn't have any
        const emptyQuestions = [
          { question: "", answer: "", id: null },
          { question: "", answer: "", id: null },
          { question: "", answer: "", id: null }
        ];
        setQuestions(emptyQuestions);
        setOriginalQuestions(emptyQuestions);
      }
      
    } catch (error) {
      console.error('Error loading user data:', error);
      console.error('Error details:', error.response?.data || error.message);
      setErrors({ general: `Failed to load user data: ${error.message}` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleQuestionChange = (index, question) => {
    const newQuestions = [...questions];
    newQuestions[index].question = question;
    setQuestions(newQuestions);
    
    if (questionErrors[index]?.questionError) {
      const newQuestionErrors = [...questionErrors];
      newQuestionErrors[index] = { ...newQuestionErrors[index], questionError: '' };
      setQuestionErrors(newQuestionErrors);
    }
  };

  const handleAnswerChange = (index, answer) => {
    const newQuestions = [...questions];
    newQuestions[index].answer = answer;
    setQuestions(newQuestions);
    
    if (questionErrors[index]?.answerError) {
      const newQuestionErrors = [...questionErrors];
      newQuestionErrors[index] = { ...newQuestionErrors[index], answerError: '' };
      setQuestionErrors(newQuestionErrors);
    }
  };

  const handleVerificationAnswerChange = (index, value) => {
    const newAnswers = [...verificationAnswers];
    newAnswers[index] = value;
    setVerificationAnswers(newAnswers);
    
    if (verificationErrors[index]) {
      const newVerificationErrors = [...verificationErrors];
      newVerificationErrors[index] = '';
      setVerificationErrors(newVerificationErrors);
    }
  };

  const verifyAnswer = (questionIndex) => {
    const userAnswer = verificationAnswers[questionIndex].trim();
    const originalAnswer = originalQuestions[questionIndex]?.answer;
    
    if (userAnswer === originalAnswer) {
      const newVerified = [...verifiedQuestions];
      newVerified[questionIndex] = true;
      setVerifiedQuestions(newVerified);
      
      const newVerificationErrors = [...verificationErrors];
      newVerificationErrors[questionIndex] = '';
      setVerificationErrors(newVerificationErrors);
      
      return true;
    } else {
      const newVerificationErrors = [...verificationErrors];
      newVerificationErrors[questionIndex] = 'Incorrect answer. Please enter correct information.';
      setVerificationErrors(newVerificationErrors);
      
      const newVerified = [...verifiedQuestions];
      newVerified[questionIndex] = false;
      setVerifiedQuestions(newVerified);
      return false;
    }
  };

  const handleCheckboxChange = (index, checked) => {
    const newCheckboxes = [...enableEditCheckboxes];
    newCheckboxes[index] = checked;
    setEnableEditCheckboxes(newCheckboxes);
    
    if (!checked) {
      // Reset verification states when unchecking
      const newAnswers = [...verificationAnswers];
      newAnswers[index] = '';
      setVerificationAnswers(newAnswers);
      
      const newVerificationErrors = [...verificationErrors];
      newVerificationErrors[index] = '';
      setVerificationErrors(newVerificationErrors);
      
      const newVerified = [...verifiedQuestions];
      newVerified[index] = false;
      setVerifiedQuestions(newVerified);
      
      // Reset question and answer to original values
      const newQuestions = [...questions];
      newQuestions[index] = { 
        question: originalQuestions[index]?.question || '', 
        answer: originalQuestions[index]?.answer || '',
        id: originalQuestions[index]?.id || null
      };
      setQuestions(newQuestions);
      
      // Also clear any verification errors for this question
      const newQuestionErrors = [...questionErrors];
      newQuestionErrors[index] = {};
      setQuestionErrors(newQuestionErrors);
    }
  };

  const handleCancelEdit = (index) => {
    setEditingQuestion(null);
    const newQuestions = [...questions];
    newQuestions[index] = { ...originalQuestions[index] };
    setQuestions(newQuestions);
  };

  const handleVerifyAndEdit = (index) => {
    verifyAnswer(index);
  };

  const handleAnswerVerification = (index) => {
    const userAnswer = verificationAnswers[index].trim();
    const originalAnswer = originalQuestions[index]?.answer;
    
    if (userAnswer === originalAnswer) {
      const newVerified = [...verifiedQuestions];
      newVerified[index] = true;
      setVerifiedQuestions(newVerified);
      
      const newVerificationErrors = [...verificationErrors];
      newVerificationErrors[index] = '';
      setVerificationErrors(newVerificationErrors);
      
      // Copy the verified answer to the main answer field
      const newQuestions = [...questions];
      newQuestions[index].answer = userAnswer;
      setQuestions(newQuestions);
      
      return true;
    } else {
      const newVerificationErrors = [...verificationErrors];
      newVerificationErrors[index] = 'Incorrect answer. Please enter the correct answer.';
      setVerificationErrors(newVerificationErrors);
      
      const newVerified = [...verifiedQuestions];
      newVerified[index] = false;
      setVerifiedQuestions(newVerified);
      return false;
    }
  };

  const validateProfile = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateQuestions = () => {
    const newQuestionErrors = [];
    let isValid = true;
    
    questions.forEach((q, index) => {
      const questionError = {};
      
      // If checkbox is enabled and verified, both question and answer must be provided
      if (enableEditCheckboxes[index] && verifiedQuestions[index]) {
        if (!q.question.trim()) {
          questionError.questionError = 'Please select a question';
          isValid = false;
        }
        
        if (!q.answer.trim()) {
          questionError.answerError = 'Please provide an answer';
          isValid = false;
        }
      }
      
      // If checkbox is enabled but not verified, verification answer is required
      if (enableEditCheckboxes[index] && !verifiedQuestions[index]) {
        if (!verificationAnswers[index].trim()) {
          questionError.answerError = 'Please enter your current answer for verification';
          isValid = false;
        }
      }
      
      newQuestionErrors[index] = questionError;
    });
    
    setQuestionErrors(newQuestionErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfile()) {
      return;
    }
    
    // Check if any enabled checkboxes are not verified
    const hasUnverifiedEnabledCheckboxes = enableEditCheckboxes.some((enabled, index) => 
      enabled && !verifiedQuestions[index]
    );
    
    if (hasUnverifiedEnabledCheckboxes) {
      setErrors({ general: 'Please complete verification for all enabled security questions before submitting.' });
      return;
    }
    
    // Validate questions for enabled and verified checkboxes
    if (!validateQuestions()) {
      return;
    }
    
    try {
      const token = sessionStorage.getItem("token");
      
      // Prepare the data according to the backend API structure
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        address: formData.address,
        email: formData.email,
        security_questions: questions.map((q, index) => {
          // Always find the security question ID from available questions for the current question text
          const securityQuestion = securityQuestions.find(sq => sq.question === q.question);
          
          if (securityQuestion) {
            return {
              security_question_id: securityQuestion.id,
              answer: q.answer
            };
          }
          
          // If no matching question found, return null (will be filtered out)
          return null;
        }).filter(q => q !== null && q.answer) // Only include questions with valid IDs and answers
      };
      
      console.log('Submitting data:', submitData);
      console.log('Security questions being sent:', submitData.security_questions);
      console.log('Available security questions:', securityQuestions);
      
      const response = await updateUserProfileData(token, submitData);
      console.log('Update response:', response.data);
      
      if (response.data.status === 'success') {
        // Update session variables with new user data
        const updatedUser = response.data.user;
        
        // Update session storage with new user data
        if (updatedUser.first_name) {
          sessionStorage.setItem('user_name', updatedUser.first_name);
        }
        if (updatedUser.email) {
          sessionStorage.setItem('user_email', updatedUser.email);
        }
        
        console.log('Session variables updated:', {
          user_name: sessionStorage.getItem('user_name'),
          user_email: sessionStorage.getItem('user_email')
        });
        
        // Reload user data to get updated security questions
        await loadUserData();
        
        navigate('/course/home');
      } else {
        setErrors({ general: response.data.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      if (error.response?.data?.errors) {
        setErrors({ general: error.response.data.errors });
      } else {
        setErrors({ general: 'Failed to update profile. Please try again.' });
      }
    } finally {
      setIsLoader(false);
    }
  };

  const getSelectedQuestions = () => {
    return questions.map(q => q.question).filter(q => q.trim() !== '');
  };

  if (isLoading) {
    return (
      <div className="min-vh-100 bg-light">
        <Header />
        <div className="container mt-5">
          <div className="row justify-content-center">
            <div className="col-md-6 text-center">
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <span className="spinner-border text-primary-s" role="status">
                  <span className="visually-hidden">Loading...</span>
                </span>
              </div>
              <p className="mt-3">Loading your profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <Header />
      
      {isLoader && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999}}>
          <div className="bg-white p-4 rounded text-center">
            <img src={Loader} alt="Loader" />
          </div>
        </div>
      )}
      
      <div className="container mt-4">
        <div className="row mt-5">
          <div className="col-lg-10 mx-auto mt-5">
            {/* Welcome Section */}
            <div className="text-center mb-5 mt-5">
              <div className="d-inline-flex align-items-center justify-content-center bg-primary-s rounded-circle mb-3" style={{width: '80px', height: '80px'}}>
                <i className="fa fa-user fa-2x text-white"></i>
              </div>
              <h2 className="fw-bold text-dark mb-2">Edit Your Profile</h2>
              <p className="text-muted fs-5">Keep your information up to date and secure</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Profile Information Card */}
              <div className="bg-white rounded-4 shadow-sm border-0 mb-4">
                <div className="card-header bg-gradient-primary-s text-white rounded-top-4">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-user-edit fa-lg me-3"></i>
                    <div>
                      <h5 className="mb-0 fw-bold">Personal Information</h5>
                      <small className="opacity-75">Update your basic details</small>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {errors.general && <div className="alert alert-danger">{errors.general}</div>}
                  
                  {isTestCompleted && (
                    <div className="alert alert-info d-flex align-items-center mb-4">
                      <i className="fa fa-info-circle me-2"></i>
                      <span>Profile information is locked because you have completed the test. Contact support if you need to make changes.</span>
                    </div>
                  )}
                  
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input 
                          type="text" 
                          className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                          id="first_name" 
                          placeholder=" " 
                          name="first_name" 
                          value={formData.first_name} 
                          onChange={handleChange}
                          disabled={isTestCompleted}
                        />
                        <label htmlFor="first_name">
                          <i className="fa fa-user me-2 text-primary-s"></i>First Name
                        </label>
                        {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input 
                          type="text" 
                          className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                          id="last_name" 
                          placeholder=" " 
                          name="last_name" 
                          value={formData.last_name} 
                          onChange={handleChange}
                          disabled={isTestCompleted}
                        />
                        <label htmlFor="last_name">
                          <i className="fa fa-user me-2 text-primary-s"></i>Last Name
                        </label>
                        {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <input 
                          type="email" 
                          className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                          id="email" 
                          placeholder=" " 
                          name="email" 
                          disabled={true}
                          value={formData.email} 
                          onChange={handleChange}
                        />
                        <label htmlFor="email">
                          <i className="fa fa-envelope me-2 text-primary-s"></i>Email Address
                        </label>
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <input 
                          ref={addressInputRef}
                          type="text" 
                          className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                          id="address" 
                          placeholder=" " 
                          name="address" 
                          value={formData.address} 
                          onChange={handleChange}
                          autoComplete="off"
                          disabled={isTestCompleted}
                          onFocus={() => {
                            if (isGoogleMapsLoaded && !autocompleteRef.current && !isTestCompleted) {
                              setTimeout(() => {
                                initializeAutocomplete();
                              }, 50);
                            }
                          }}
                        />
                        <label htmlFor="address">
                          <i className="fa fa-map-marker-alt me-2 text-primary-s"></i>
                          Address
                        </label>
                        {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Questions Card */}
              <div className="bg-white rounded-4 shadow-sm border-0 mb-4 p-3">
                <div className="card-header bg-gradient-warning text-dark rounded-top-4">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-shield-alt fa-lg me-3"></i>
                    <div>
                      <h5 className="mb-0 fw-bold">Security Questions</h5>
                      <small className="opacity-75">Protect your account with security questions</small>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {isTestCompleted && (
                    <div className="alert alert-info d-flex align-items-center mb-4">
                      <i className="fa fa-info-circle me-2"></i>
                      <span>Security questions are locked because you have completed the test. Contact support if you need to make changes.</span>
                    </div>
                  )}
                  {!isTestCompleted && (
                    <div className="alert alert-info d-flex align-items-center mb-4">
                      <i className="fa fa-info-circle me-2"></i>
                      <span>Check the "Enable Edit" checkbox for any question you want to modify. You'll need to verify your current answer first.</span>
                    </div>
                  )}

                  {questions.map((q, index) => {
                    const selectedQuestions = getSelectedQuestions();
                    const isCheckboxEnabled = enableEditCheckboxes[index];
                    const isVerified = verifiedQuestions[index];
                    
                    return (
                      <div className={`mb-4 p-4 border rounded-3 ${isCheckboxEnabled ? 'border-primary bg-light' : 'border-light'}`} key={index}>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <div className="bg-primary-s text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '32px', height: '32px'}}>
                                <span className="fw-bold">{index + 1}</span>
                              </div>
                              <label className="form-label fw-bold mb-0">Security Question {index + 1}</label>
                            </div>
                            <div className="ms-5">
                              <div className="p-3 bg-light rounded-2">
                                <strong className="text-primary-s">{q.question || "No question selected"}</strong>
                              </div>
                            </div>
                          </div>
                          <div className="ms-3">
                            <div className="form-check">
                              <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id={`enableEdit${index}`}
                                checked={isCheckboxEnabled}
                                onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                                disabled={isTestCompleted}
                              />
                              <label className="form-check-label" htmlFor={`enableEdit${index}`}>
                                Enable Edit
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        {/* Question dropdown - always visible but disabled when checkbox is not checked */}
                        <div className="ms-5 mb-3">
                          <div className="form-floating">
                            <select
                              className={`form-select ${questionErrors[index]?.questionError ? 'is-invalid' : ''}`}
                              value={q.question}
                              onChange={(e) => handleQuestionChange(index, e.target.value)}
                              disabled={isTestCompleted || !isCheckboxEnabled || !isVerified}
                            >
                              <option value="" disabled>
                                Select a question...
                              </option>
                              {securityQuestions
                                .filter(
                                  (question) =>
                                    !selectedQuestions.includes(question.question) ||
                                    question.question === q.question
                                )
                                .map((question, i) => (
                                  <option key={question.id} value={question.question}>
                                    {question.question}
                                  </option>
                                ))}
                            </select>
                            <label>Security Question</label>
                            {questionErrors[index]?.questionError && (
                              <div className="invalid-feedback">{questionErrors[index].questionError}</div>
                            )}
                          </div>
                        </div>

                        {/* Answer field - always visible but disabled when checkbox is not checked */}
                        <div className="ms-5 mb-3">
                          <div className="form-floating">
                            <input
                              type="password"
                              className={`form-control ${questionErrors[index]?.answerError ? 'is-invalid' : ''}`}
                              placeholder=" "
                              value={
                                !isCheckboxEnabled 
                                  ? originalQuestions[index]?.answer || '' 
                                  : (isCheckboxEnabled && !isVerified 
                                      ? verificationAnswers[index] 
                                      : q.answer)
                              }
                              onChange={(e) => {
                                if (isCheckboxEnabled && !isVerified) {
                                  handleVerificationAnswerChange(index, e.target.value);
                                } else {
                                  handleAnswerChange(index, e.target.value);
                                }
                              }}
                              disabled={isTestCompleted || !isCheckboxEnabled}
                            />
                            <label>Answer</label>
                            {questionErrors[index]?.answerError && (
                              <div className="invalid-feedback">{questionErrors[index].answerError}</div>
                            )}
                            {verificationErrors[index] && (
                              <div className="text-danger mt-1">{verificationErrors[index]}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Verification step - only show when checkbox is enabled but not verified */}
                        {isCheckboxEnabled && !isVerified && (
                          <div className="ms-5">
                            <div className="alert alert-warning d-flex align-items-center mb-3">
                              <i className="fa fa-lock me-2"></i>
                              <span>Please enter your current answer to enable question editing</span>
                            </div>
                            <button 
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleAnswerVerification(index)}
                              disabled={isTestCompleted}
                            >
                              <i className="fa fa-check me-1"></i> Verify Answer
                            </button>
                            {!verificationAnswers[index].trim() && (
                              <div className="text-danger mt-2">
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                Answer field cannot be empty for verification
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Success message when verified */}
                        {isCheckboxEnabled && isVerified && (
                          <div className="ms-5">
                            <div className="alert alert-success d-flex align-items-center mb-3">
                              <i className="fa fa-check-circle me-2"></i>
                              <span>Answer verified! You can now edit the question and set a new answer.</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary btn-lg px-4"
                  onClick={() => navigate("/course/home")}
                >
                  <i className="fa fa-arrow-left me-2"></i> Back to Dashboard
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary-s btn-lg px-5"
                  disabled={isLoader}
                >
                  {isLoader ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-2"></i> Update Profile
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;